import React, { useEffect, useRef, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { ColorNordicGrey1 } from 'autogen/design-tokens/tokens';
import type { ProductInformationViewModel, ProductTabModel, ProductTabType } from 'autogen/swagger/all';
import { useProductPageExpectedDeliveryQuickView } from 'components/expecteddelivery/shared/hooks/useProductPageExpectedDeliveryQuickView';
import FollowButton from 'components/FollowButton';
import FollowButtonWithTooltip from 'components/FollowButton/FollowButtonWithTooltip';
import Flex from 'components/Layout/components/Flex';
import useIsMobile from 'hooks/responsive/useIsMobile';
import useGA4Tracking from 'modules/tracking/GA4';
import TrackingContext from 'modules/tracking/GA4/helpers/TrackingContext';
import type { ReadMoreType } from 'modules/tracking/GA4/types/ReadMoreType';
import MinimumSalesQuantityDescription from '../minimumSalesQuantityDescription/MinimumSalesQuantityDescription';
import ReviewReport from '../ReviewReport';
import { activeClassName } from './components/shared/variables';
import ProductAccordionItem from './components/tabs/ProductAccordionItem';
import SimpleItem from './components/tabs/SimpleItem';
import { ProductAccordionContext } from './hooks/context';
import useShouldAnimate from './hooks/useShouldAnimate';
import useThemeHandler from './themes/useThemeHandler';

const StyledSimpleItem = styled(SimpleItem)``;
const StyledProductAccordionItem = styled(ProductAccordionItem)``;
const StyledReviewReport = styled(ReviewReport)``;
const StyledMinimumSalesQuantityDescription = styled(MinimumSalesQuantityDescription)``;

const Wrapper = styled.div`
    ${({ theme }) => (theme as any)?.Wrapper}

    ${StyledProductAccordionItem}, ${StyledSimpleItem}, ${StyledReviewReport}, ${StyledMinimumSalesQuantityDescription} {
        border-bottom: 1px solid ${ColorNordicGrey1};
        
        &:last-child {
            border-bottom: 0px;
        }
    }

`;

function getActiveTabFromModel(productTabs: ProductTabModel[]) {
    const activeItems = productTabs
        .filter((productTab) => productTab.isActive === true)
        .map((productTab) => productTab.type);

    return activeItems;
}

export const productAccordionAnchorId = 'ProductAccordion';

export default function ProductAccordion({ model, ...restProps }: { model: ProductInformationViewModel }): JSX.Element {
    const { productTabs, minimumSalesQuantityDescription, productReviewSummaryOverview, followBrandClubModel } = model;
    const [activeTab, setActiveTab] = useState<ProductTabType[]>(getActiveTabFromModel(productTabs));
    const shouldAnimate = useShouldAnimate();
    const GA4Tracking = useGA4Tracking();

    const [scrollPosition, setScrollPosition] = useState<number>();
    const isMobile = useIsMobile();
    const ref = useRef<HTMLDivElement>(null);
    const theme = useThemeHandler();
    const toggle = useProductPageExpectedDeliveryQuickView();

    function handleShippingOptions() {
        toggle(true);
    }

    function trackTabChange(productTabType: ProductTabType, isOpen: boolean) {
        GA4Tracking({
            category: 'navigation',
            eventName: 'tab_click',
            eventParams: [
                { param_name: 'tab_name', param_value: productTabType },
                {
                    param_name: 'toggle',
                    param_value: isOpen
                }
            ]
        });
    }

    function handleTabChange(newType: ProductTabType) {
        if (!activeTab.includes(newType)) {
            // Open tab
            setActiveTab([newType]);
            setScrollPosition(Number.NaN);
            trackTabChange(newType, true);
            return;
        }

        if (activeTab.includes(newType)) {
            // close active tab
            setActiveTab((prevState) => prevState.filter((type) => type !== newType));
            setScrollPosition(Number.NaN);
            trackTabChange(newType, false);
            return;
        }

        if (ref?.current) {
            const activeContentHeight = ref.current
                .getElementsByClassName(activeClassName)[0]
                ?.getBoundingClientRect().height;
            requestAnimationFrame(() => {
                setScrollPosition(activeContentHeight);
                setActiveTab([newType]);
            });
        }
    }

    useEffect(() => {
        if (scrollPosition && isMobile) {
            window.scrollBy({
                top: -scrollPosition
            });
        }
    }, [scrollPosition, isMobile]);

    function filterDeliveryAndReturn(type: ProductTabModel['type']) {
        return type === 'DeliveryAndReturn' || type === 'ShippingOptions';
    }

    return (
        <ProductAccordionContext.Provider value={(newType) => handleTabChange(newType)}>
            <ThemeProvider theme={theme as any}>
                <Wrapper {...restProps} ref={ref} id={productAccordionAnchorId}>
                    {productTabs
                        ?.filter(({ type }) => !filterDeliveryAndReturn(type))
                        .map((productTab: ProductTabModel, index) => {
                            const { type } = productTab;
                            const isActive = activeTab.includes(type);
                            const animate = shouldAnimate();

                            return (
                                <TrackingContext
                                    key={type + index}
                                    eventParams={[
                                        { param_name: 'read_more_type', param_value: productTab.title as ReadMoreType }
                                    ]}
                                >
                                    <StyledProductAccordionItem
                                        // eslint-disable-next-line react/no-array-index-key
                                        {...{ ...productTab, isActive }}
                                        animate={animate}
                                    />
                                </TrackingContext>
                            );
                        })}
                    {productTabs
                        ?.filter(({ type }) => filterDeliveryAndReturn(type))
                        .map((x) => (
                            <StyledSimpleItem
                                forwardedAs="button"
                                key={x.type}
                                type="button"
                                onClick={() => {
                                    handleShippingOptions();
                                }}
                            >
                                {x.title}
                            </StyledSimpleItem>
                        ))}
                    {productReviewSummaryOverview && <StyledReviewReport {...productReviewSummaryOverview} />}
                    {minimumSalesQuantityDescription && (
                        <StyledMinimumSalesQuantityDescription
                            minimumSalesQuantityDescription={minimumSalesQuantityDescription}
                            hasTopBorder={!!productReviewSummaryOverview}
                        />
                    )}
                    {followBrandClubModel && (
                        <StyledSimpleItem>
                            <Flex justifyContent="space-between" alignItems="center">
                                {followBrandClubModel.brandClubName}{' '}
                                {followBrandClubModel.isFollowing ? (
                                    <FollowButton {...followBrandClubModel} />
                                ) : (
                                    <FollowButtonWithTooltip {...followBrandClubModel} />
                                )}
                            </Flex>
                        </StyledSimpleItem>
                    )}
                </Wrapper>
            </ThemeProvider>
        </ProductAccordionContext.Provider>
    );
}
